import { useAuth } from '@hourly/contexts/AuthContext';
import { Button, ButtonSize, ButtonVariant } from '@hourly-hds/components';
import Plaid, {
  CustomPlaidLinkError,
  CustomPlaidLinkOnSuccessMetadata,
} from '@shared/components/Plaid';
import { useBankAccount } from '@shared/data/hooks/useBankAccount';
import StepLayout from '../_shared/StepLayout';
import { Step } from '../data/models/step.model';
import BankAccountCard, { type VerificationStatus } from './components/BankAccountCard';

interface BankAccountProps {
  step: Step;
  onContinue: () => void;
  onPrevious: () => void;
}

const BankAccount = ({ step, onContinue, onPrevious }: BankAccountProps) => {
  const {
    sessionState: { partnerId },
  } = useAuth();

  const {
    token,
    isAwaitingMicrodepositVerification,
    isBankAccountRequestLoading,
    cleanToken,
    getBankAccountToken,
    addBankAccount,
    loadSameDayMicrodepositToken,
    startSameDayMicrodeposit,
    deleteBankAccountValidationToken,
  } = useBankAccount(partnerId!, { requestSameDayMicrodepositOnMount: step.isStepPending });

  const isWaitingForMicrodepositVerification =
    step.isStepPending && isAwaitingMicrodepositVerification;

  let bankAccountVerification: VerificationStatus = 'Idle';
  if (isWaitingForMicrodepositVerification) {
    bankAccountVerification = 'Pending';
  }

  if (step.isStepCompleted) {
    bankAccountVerification = 'Successfully';
  }

  const handleExitPlaid = async (exitError: CustomPlaidLinkError | null) => {
    if (exitError?.error_code === 'TOO_MANY_VERIFICATION_ATTEMPTS') {
      await deleteBankAccountValidationToken();
      return;
    }

    cleanToken();
  };

  const handleSuccessAddBankAccount = (
    publicToken: string,
    metadata: CustomPlaidLinkOnSuccessMetadata
  ) => {
    if (metadata.account.verification_status === 'pending_manual_verification') {
      startSameDayMicrodeposit(publicToken);
      return;
    }

    addBankAccount(publicToken, {
      onSuccess: () => {
        onContinue();
      },
    });
  };

  return (
    <StepLayout>
      <StepLayout.Header>
        <StepLayout.Title>Add Your Business&apos; Bank Account</StepLayout.Title>
        <StepLayout.Description>
          {isWaitingForMicrodepositVerification
            ? `You'll need to verify the bank account. Please come back once\nthe $0.01 micro-deposit appears in your movements`
            : `This is the account that will be used for paying you revenue share. \nYou can change it later.`}
        </StepLayout.Description>
      </StepLayout.Header>
      <StepLayout.Content>
        <BankAccountCard verificationStatus={bankAccountVerification} />
        {token && (
          <Plaid
            token={token}
            onSuccess={(publicToken, metadata) =>
              handleSuccessAddBankAccount(publicToken, metadata)
            }
            onExit={handleExitPlaid}
          />
        )}
      </StepLayout.Content>
      <StepLayout.Actions>
        {!isWaitingForMicrodepositVerification && (
          <>
            <Button
              fullWidth
              label="Previous"
              disabled={isBankAccountRequestLoading}
              size={ButtonSize.Large}
              onClick={onPrevious}
            />
            <Button
              fullWidth
              label="Continue"
              disabled={isBankAccountRequestLoading}
              variant={ButtonVariant.Primary}
              size={ButtonSize.Large}
              onClick={step.isStepCompleted ? onContinue : getBankAccountToken}
            />
          </>
        )}

        {isWaitingForMicrodepositVerification && (
          <Button
            fullWidth
            label="Verify"
            variant={ButtonVariant.Primary}
            size={ButtonSize.Large}
            disabled={isBankAccountRequestLoading}
            onClick={loadSameDayMicrodepositToken}
          />
        )}
      </StepLayout.Actions>
    </StepLayout>
  );
};

export default BankAccount;
